'use client'

import { useState, useEffect } from 'react'
import { useTranslations } from 'next-intl'
import { toast } from 'sonner'
import { MailCheckIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import LocaleCombobox from '@/components/ui/locale-combobox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { useSendEmailVerification } from '@/hooks/use-users'
import { useMetaStore } from '@/store'
import { RoleEnum } from '@/enums'

type PropsType = {
  userId: string
  userRoles?: RoleEnum[]
  open: boolean
  onOpenChange: (open: boolean) => void
}

type SourceType = 'frontend' | 'admin'

const SendEmailVerificationDialog = ({ userId, userRoles, open, onOpenChange }: PropsType) => {
  const t = useTranslations()
  const { metaState } = useMetaStore()
  const { mutate: sendVerification, isPending } = useSendEmailVerification()

  const appDefaultLocale = metaState.response?.generalSettings.appDefaultLocale ?? ''
  const adminDefaultLocale = metaState.response?.generalSettings.adminDefaultLocale ?? ''
  const isAdmin = userRoles?.some(r => r === RoleEnum.ADMIN || r === RoleEnum.ROOT) ?? false

  const [source, setSource] = useState<SourceType>('frontend')
  const [locale, setLocale] = useState<string>(appDefaultLocale)

  useEffect(() => {
    setLocale(source === 'admin' ? adminDefaultLocale : appDefaultLocale)
  }, [source, appDefaultLocale, adminDefaultLocale])

  useEffect(() => {
    if (open) {
      setSource('frontend')
      setLocale(appDefaultLocale)
    }
  }, [open, appDefaultLocale])

  useEffect(() => {
    if (!isAdmin && source === 'admin') {
      setSource('frontend')
    }
  }, [isAdmin, source])

  const handleConfirm = () => {
    sendVerification(
      { userId, params: { source, locale } },
      {
        onSuccess: () => {
          toast.success(t('email_verification_sent'))
          onOpenChange(false)
        },
        onError: () => {
          toast.error(t('error_sending_email'))
        }
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('send_email_verification')}</DialogTitle>
          <DialogDescription>{t('send_email_verification_description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="verification-source">{t('source')}</Label>
            <Select value={source} onValueChange={(val: SourceType) => setSource(val)}>
              <SelectTrigger id="verification-source" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="frontend">{t('source_frontend')}</SelectItem>
                {isAdmin && <SelectItem value="admin">{t('source_admin')}</SelectItem>}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="verification-locale">{t('language')}</Label>
            <LocaleCombobox id="verification-locale" value={locale} onValueChange={setLocale} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            {t('cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={isPending || !locale}>
            <MailCheckIcon className="h-4 w-4" />
            {isPending ? t('sending') : t('send')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default SendEmailVerificationDialog
